import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Paper } from "@mui/material";
import CustomTextArea from "../../app/common/form/CustomTextArea";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import CustomSelectInput from "../../app/common/form/CustomSelectInput";
import * as Yup from "yup";
import { v4 as uuid } from "uuid";
import { Payment, PaymentFormValues } from "../../app/models/payment";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import CustomDateInput from "../../app/common/form/CustomDateInput";
import {
  usePaymentMethodOptions,
  usePaymentTypeOptions,
} from "../../app/common/enums/paymentType";
import { useTranslation } from "react-i18next";
import agent from "../../app/api/agent";

interface Props {
  id?: string;
}

export default function PaymentForm({ id }: Props) {
  const { t } = useTranslation();
  const [payment, setPayment] = useState<PaymentFormValues>(
    new PaymentFormValues()
  );
  const paymentStore = useStore((s) => s.paymentStore);
  const modalStore = useStore((s) => s.modalStore);
  const memberStore = useStore((s) => s.memberStore);

  const { createPayment, updatePayment, loading } = paymentStore;
  const commonRequiredMessage = t("paymentForm.requiredMessage");
  const validationSchema = Yup.object({
    paidAmount: Yup.number().required(
      `${t("paymentForm.paidAmountLabel")} ${commonRequiredMessage}`
    ),
    paidAt: Yup.date().required(
      `${t("paymentForm.paymentDateLabel")} ${commonRequiredMessage}`
    ),
  });
  const paymentMethodOptions = usePaymentMethodOptions();
  const paymentTypeOptions = usePaymentTypeOptions();
  const handleFormSubmit = async (paymentValues: PaymentFormValues) => {
    if (!paymentValues.id) {
      const newPayment: Payment = {
        ...paymentValues,
        id: uuid(),
        memberId: memberStore.selectedMember?.id!,
      };
      await createPayment(newPayment as any);
      modalStore.closeModal();
    } else {
      const updated: Payment = { ...paymentValues, id };
      await updatePayment(updated as any);
      modalStore.closeModal();
    }
  };

  useEffect(() => {
    if (!id) return;
    agent.Payments.details(id).then((p: any) => setPayment(new PaymentFormValues(p)));
  }, [id]);

  if (loading) return <LoadingComponent content={t("general.loading")} />;

  return (
    <Paper sx={{ p: 2 }}>
      <Formik
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={payment}
        onSubmit={(values) => handleFormSubmit(values)}
      >
        {({ handleSubmit, isValid, isSubmitting, dirty }) => (
          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            style={{ paddingBottom: "35px" }}
          >
            <div className="three fields">
              <CustomSelectInput
                label={t("paymentForm.paymentTypeLabel")}
                options={paymentTypeOptions}
                name="paymentType"
                placeholder={t("paymentForm.paymentTypeLabel")}
              />
              <CustomDateInput
                name="paidAt"
                label={t("paymentForm.paymentDateLabel")}
                placeholderText={t("paymentForm.paymentDateLabel")}
                dateFormat="dd.MM.yyyy"
              />
              <CustomTextInput
                type="number"
                name="paidAmount"
                placeholder={t("paymentForm.paidAmountPlaceholder")}
                label={t("paymentForm.paidAmountLabel")}
              />
            </div>

            <div className="three fields">
              <CustomTextInput
                type="text"
                name="bookingText"
                placeholder={t("paymentForm.bookingTextLabel")}
                label={t("paymentForm.bookingTextLabel")}
              />
              <CustomTextInput
                type="text"
                name="reasonForPayment"
                placeholder={t("paymentForm.reasonForPaymentLabel")}
                label={t("paymentForm.reasonForPaymentLabel")}
              />
              <CustomSelectInput
                label={t("paymentForm.paymentMethodLabel")}
                options={paymentMethodOptions}
                name="paymentMethod"
                placeholder={t("paymentForm.paymentMethodLabel")}
              />
            </div>

            {id && !payment.memberId && (
              <div className="three fields">
                <CustomTextInput
                  type="text"
                  name="iban"
                  placeholder={t("paymentForm.ibanLabel")}
                  label={t("paymentForm.ibanLabel")}
                />

                <CustomTextInput
                  type="number"
                  name="memberNumber"
                  placeholder={t("paymentForm.memberNumberLabel")}
                  label={t("paymentForm.memberNumberLabel")}
                />
              </div>
            )}

            <CustomTextArea
              label={t("paymentForm.notesLabel")}
              rows={3}
              name="note"
              placeholder={t("paymentForm.notesLabel")}
            />

            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting || !dirty || !isValid}
              type="submit"
              sx={{ float: "right", mt: 2, ml: 2 }}
            >
              {t("general.confirm")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={modalStore.closeModal}
              type="button"
              sx={{ float: "right", mt: 2 }}
            >
              {t("general.cancel")}
            </Button>
          </form>
        )}
      </Formik>
    </Paper>
  );
}
